import { PatientDynamicFieldType } from "@/types/enums";
import type { DynamicHealthFieldValue, PatientChart } from "@/types/patient";

/**
 * Extract specific health field values from patient's dynamic health data
 * Handles type casting for our predefined health fields (which should be string arrays)
 */
export function getHealthFieldValues(
  patient: PatientChart,
  fieldType: PatientDynamicFieldType,
): string[] {
  if (!patient.healthData) {
    return [];
  }

  // Search for the field by type instead of by field name
  let fieldData: any = null;
  for (const [_, data] of Object.entries(patient.healthData)) {
    if (data && typeof data === "object" && data.type === fieldType) {
      fieldData = data;
      break;
    }
  }

  // Handle new data structure with {values: string[], type: string}
  if (fieldData && typeof fieldData === "object" && "values" in fieldData) {
    const values = fieldData.values;
    if (Array.isArray(values)) {
      return values.map(String).filter((v) => v && v.trim()); // Filter out empty strings
    }
  }

  // Fallback: try old approach with field name (for backward compatibility)
  const key = fieldType.toLowerCase();
  const fallbackFieldData = patient.healthData[key];

  if (Array.isArray(fallbackFieldData)) {
    return fallbackFieldData.map(String); // Convert any non-string items to strings
  } else if (
    fallbackFieldData !== null &&
    fallbackFieldData !== undefined &&
    typeof fallbackFieldData === "string"
  ) {
    return [String(fallbackFieldData)]; // Convert single value to string array
  }

  return [];
}

export function getHealthFieldValueDetails(
  patient: PatientChart,
  fieldType: PatientDynamicFieldType,
): DynamicHealthFieldValue[] {
  if (!patient.healthData) return [];

  for (const fieldData of Object.values(patient.healthData)) {
    if (fieldData?.type === fieldType) {
      if (fieldData.valueDetails?.length === fieldData.values.length) {
        return fieldData.valueDetails;
      }
      return fieldData.values.map((value) => ({
        value,
        conceptRxId: null,
        strengthFormRxId: null,
      }));
    }
  }

  return [];
}

/**
 * Get a generic health field value as-is (for custom fields)
 * Returns the raw value without type casting to preserve flexibility
 */
export function getHealthFieldValue(
  patient: PatientChart,
  fieldKey: string,
): any {
  if (!patient.healthData) {
    return null;
  }
  return patient.healthData[fieldKey];
}

/** Entries of a field that e-prescribing holds as free text and so can't screen prescriptions against. */
export function getUnscreenedValues(
  patient: PatientChart,
  fieldType: PatientDynamicFieldType,
): Set<string> {
  const field = Object.values(patient.healthData ?? {}).find(
    (data) => data?.type === fieldType,
  );
  return new Set(field?.unscreenedValues ?? []);
}

export function getAllergies(patient: PatientChart): string[] {
  return getHealthFieldValues(patient, PatientDynamicFieldType.ALLERGIES);
}

export function getMedicalHistory(patient: PatientChart): string[] {
  return getHealthFieldValues(patient, PatientDynamicFieldType.MEDICAL_HISTORY);
}

export function getFamilyHistory(patient: PatientChart): string[] {
  return getHealthFieldValues(patient, PatientDynamicFieldType.FAMILY_HISTORY);
}

// Social history isn't part of PatientDynamicFieldType (so it stays visible in the
// chart's "Additional Health Information" catch-all); read it by its raw field type.
export function getSocialHistory(patient: PatientChart): string[] {
  return getHealthFieldValues(
    patient,
    "SOCIAL_HISTORY" as PatientDynamicFieldType,
  );
}

export function getPrescriptions(patient: PatientChart): string[] {
  return getHealthFieldValues(patient, PatientDynamicFieldType.PRESCRIPTIONS);
}

export function getMedications(patient: PatientChart): string[] {
  return getHealthFieldValues(patient, PatientDynamicFieldType.MEDICATIONS);
}

export function getPatientReportedSymptoms(patient: PatientChart): string[] {
  return getHealthFieldValues(
    patient,
    PatientDynamicFieldType.PATIENT_REPORTED_SYMPTOMS,
  );
}

/**
 * Get all health field keys from patient data
 * Useful for displaying all available health fields (including custom ones)
 */
export function getAllHealthFieldKeys(patient: PatientChart): string[] {
  if (!patient.healthData) {
    return [];
  }
  return Object.keys(patient.healthData);
}

/**
 * Check if a field is one of our predefined health field types
 */
export function isPredefinedHealthField(fieldKey: string): boolean {
  const predefinedKeys = Object.values(PatientDynamicFieldType).map((type) =>
    type.toLowerCase(),
  );
  return predefinedKeys.includes(fieldKey.toLowerCase());
}

/**
 * Get the field ID for a specific health field type
 * This is useful for API calls that need the field ID
 */
export function getHealthFieldId(
  patient: PatientChart,
  fieldType: PatientDynamicFieldType,
): string | undefined {
  if (!patient.healthData) {
    return undefined;
  }

  for (const [_, fieldData] of Object.entries(patient.healthData)) {
    if (fieldData?.type === fieldType) {
      return fieldData.fieldId;
    }
  }

  return undefined;
}

// Note: updateHealthField function removed due to type incompatibility with new DynamicHealthField structure

/**
 * Update a generic health field by key (for custom fields)
 */
export function updateHealthFieldByKey(
  patient: PatientChart,
  fieldKey: string,
  value: any,
): PatientChart {
  const currentHealthData = patient.healthData || {};

  return {
    ...patient,
    healthData: {
      ...currentHealthData,
      [fieldKey]: value,
    },
  };
}
